import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Button, Form, FormGroup } from 'reactstrap';

import { appPropTypes, useLanguageContext } from '../../../_shared';
import { messages } from '../../../_shared/i18n/messages';

import {
	buildFormSections,
	convertDataToFormEntry,
	convertFormEntryToData,
	validateFormField,
	formShape,
} from '../../utils';

import EmbeddedFormNavigator from './components/EmbeddedFormNavigator';
import EmbeddedFormSection from './components/EmbeddedFormSection';

const propTypes = {

	/**
	 * The form.
	 */
	form: formShape.isRequired,

	/**
	 * The form entry for the current user (if any).
	 */
	formEntry: PropTypes.shape({

		/**
		 * The form entry ID.
		 */
		id: PropTypes.string,

	}),

	/**
	 * The user's product ticket for the invoice item.
	 * (course application forms only)
	 */
	userTicket: PropTypes.shape({

		/**
		 * The product ticket ID.
		 */
		id: PropTypes.string.isRequired,

	}),

	/**
	 * The user's product ticket group for the invoice item.
	 * (course application forms only)
	 */
	userTicketGroup: PropTypes.shape({

		/**
		 * The product ticket group ID.
		 */
		id: PropTypes.string.isRequired,

	}),

	/**
	 * True, if the user is the editing the the form.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if form sections should be paginated.
	 */
	isPaginated: PropTypes.bool,

	/**
	 * True, if the user is the previewing the the form.
	 */
	isPreview: PropTypes.bool,

	/**
	 * The settings for filestack.
	 */
	filestack: appPropTypes.filestack,

	/**
	 * Callback to cancel the form submission.
	 */
	onCancel: PropTypes.func,

	/**
	 * Callback to save the entry for the form.
	 */
	onSave: PropTypes.func,

};

const EmbeddedForm = (props) => {

	const {
		form,
		formEntry,
		userTicket,
		userTicketGroup,
		isEditing,
		isPaginated,
		isPreview,
		filestack,
		onCancel,
		onSave,
	} = props;

	const [data, setData] = useState(convertFormEntryToData(form, formEntry));
	const [errors, setErrors] = useState({});

	const { defaultLanguage, preferredLanguage } = useLanguageContext();

	const handleValidateSection = (sectionID, onSuccess) => {

		// Validate fields within the given form section
		const [, updatedErrors] = convertDataToFormEntry(form, formEntry, data, {
			formSectionID: sectionID,
			preferredLanguage,
			defaultLanguage,
		});

		// Check if current form entry for the section contains any errors
		const hasErrors = form.form_fields?.reduce((acc, field) => {
			return acc || updatedErrors[field.id];
		}, false);

		setErrors(updatedErrors);

		// If there are errors, then don't save it!
		if (hasErrors) {

			scrollToFirstError(updatedErrors);

		} else if (onSuccess) {

			onSuccess();

		}

	};

	const handleChange = (fieldID, fieldValue, fieldOptionID) => {

		setData((prev) => Object.assign({}, prev, {
			[fieldID]: fieldValue,
			[`${fieldID}:selectedOptionID`]: fieldOptionID
		}));

		setErrors((prev) => {

			const formField =
				form.form_fields?.find((field) => field.id === fieldID);

			// Re-run form field validation and update the input errors
			return { ...prev, ...validateFormField(formField, fieldValue) };

		});

	};

	const handleSave = (event, options = {}) => {

		if (event) {
			event.preventDefault();
			event.stopPropagation();
		}

		// Check for errors in form fields (filter by section or skip validation
		// if specified in options)
		const [, updatedErrors] = convertDataToFormEntry(form, formEntry, data, {
			...options,
			preferredLanguage,
			defaultLanguage,
		});

		// Convert the input data to the format expected for a form entry
		// for all form fields
		const [entryData] = convertDataToFormEntry(form, formEntry, data, {
			preferredLanguage,
			defaultLanguage,
		});

		// Check if the form entry contains any errors
		const hasErrors = form.form_fields?.reduce((acc, field) => {
			return acc || updatedErrors[field.id];
		}, false);

		// If there are errors, then don't save it!
		if (hasErrors) {
			scrollToFirstError(updatedErrors);
			setErrors(updatedErrors);
			return;
		}

		if (onSave) {
			onSave(entryData);
		}

	};

	// Parse the form to build the ordered list of form sections with nested fields
	const formSections = useMemo(() => buildFormSections(form), [form]);

	if (isPaginated) {

		return (
			<EmbeddedFormNavigator
				type={form.type}
				formSections={formSections}
				data={data}
				userTicket={userTicket}
				userTicketGroup={userTicketGroup}
				errors={errors}
				isEditing={isEditing}
				isPreview={isPreview}
				filestack={filestack}
				onValidate={handleValidateSection}
				onChange={handleChange}
				onSave={handleSave} />
		);

	}

	// Check if the form entry has errors (i.e. at least one)
	const hasErrors = form.form_fields?.reduce((acc, field) => {
		return acc || errors[field.id];
	}, false);

	return (
		<Form onSubmit={handleSave}>
			{formSections.map((section, index) => (
				<EmbeddedFormSection
					key={section.id}
					index={index}
					formSection={section}
					data={data}
					errors={errors}
					isEditing={isEditing}
					isPreview={isPreview}
					filestack={filestack}
					onChange={handleChange} />
			))}
			{isEditing &&
				<FormGroup>
					<Button
						color="primary"
						disabled={hasErrors}
						type="submit">
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SUBMIT_LABEL} />
					</Button>
					{onCancel &&
						<Button
							className="ms-3"
							color="neutral"
							type="button"
							outline
							onClick={onCancel}>
							<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
						</Button>
					}
				</FormGroup>
			}
		</Form>
	);

};

/**
 * Scrolls any validation errors from the form into view
 *
 * @param {object} errors     The errors in the form.
 *
 * @returns {void}
 */
function scrollToFirstError(errors) {
	try {

		// Get first error out of the errors object
		const firstError = Object.keys(errors).find((i) => errors[i] === true);
		const errorParent = document.querySelector(`[for="${firstError}"]`).parentElement;

		// Find the top of the error in the DOM
		const topOfError = errorParent.getBoundingClientRect().top;
		const currentDOMPosition = document.documentElement.scrollTop;
		const offset = 30;
		const errorPosition = topOfError + currentDOMPosition - offset;

		window.scrollTo({ top: errorPosition, behavior: 'smooth' });

	} catch (error) {

		console.error('Error encountered scrolling to form validation output.');

	}
}

EmbeddedForm.propTypes = propTypes;

export default EmbeddedForm;
